export const SPREADING_FACTOR_COLORS: Array<{ sf: number; color: string }> = [
  { sf: 7, color: '#c9e52f' },
  { sf: 8, color: '#a6d75b' },
  { sf: 9, color: '#76c68f' },
  { sf: 10, color: '#48b5c4' },
  { sf: 11, color: '#22a7f0' },
  { sf: 12, color: '#1984c5' },
];

const STATUS_COLORS: { [key: string]: string } = {
  active: '#4CAF50',
  fault: '#d10000',
  inactive: '#9e9e9e',
};

interface Rect { x: number; y: number; w: number; h: number; }
interface Point { x: number; y: number; }

const SVG_NS = 'http://www.w3.org/2000/svg';

export class MarkerFactory {
  public static getDeviceStatusColor(status: string): string {
    return STATUS_COLORS[status] || STATUS_COLORS.inactive;
  }

  public static getGatewayStatusColor(status?: string): string {
    return STATUS_COLORS[status || 'inactive'] || STATUS_COLORS.inactive;
  }

  public static getSpreadingFactorColor(spreadingFactor: number): string {
    if (spreadingFactor >= 7 && spreadingFactor <= 12) {
      const colorInfo = SPREADING_FACTOR_COLORS.find((item) => item.sf === spreadingFactor);
      return colorInfo ? colorInfo.color : '#FFFFFF';
    }
    return '';
  }

  public static createCombinedStatusMarkerSVG(statusColor: string): SVGElement {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '36');
    svg.setAttribute('height', '28');
    svg.setAttribute('viewBox', '0 0 36 36');
    svg.style.cursor = 'pointer';

    const gatewayGroup = document.createElementNS(SVG_NS, 'g');
    gatewayGroup.setAttribute('transform', 'translate(10, 8) rotate(45 16 16) scale(0.6)');
    const outerRect = document.createElementNS(SVG_NS, 'rect');
    outerRect.setAttribute('x', '6');
    outerRect.setAttribute('y', '6');
    outerRect.setAttribute('width', '24');
    outerRect.setAttribute('height', '24');
    outerRect.setAttribute('fill', statusColor);
    const innerRect = document.createElementNS(SVG_NS, 'rect');
    innerRect.setAttribute('x', '10');
    innerRect.setAttribute('y', '10');
    innerRect.setAttribute('width', '16');
    innerRect.setAttribute('height', '16');
    innerRect.setAttribute('fill', '#ffffff');
    gatewayGroup.appendChild(outerRect);
    gatewayGroup.appendChild(innerRect);

    const deviceGroup = document.createElementNS(SVG_NS, 'g');
    deviceGroup.setAttribute('transform', 'translate(0, 8) scale(0.8)');
    const outerRing = document.createElementNS(SVG_NS, 'circle');
    outerRing.setAttribute('cx', '10');
    outerRing.setAttribute('cy', '10');
    outerRing.setAttribute('r', '10');
    outerRing.setAttribute('fill', statusColor);

    const innerCircle = document.createElementNS(SVG_NS, 'circle');
    innerCircle.setAttribute('cx', '10');
    innerCircle.setAttribute('cy', '10');
    innerCircle.setAttribute('r', '7');
    innerCircle.setAttribute('fill', '#ffffff');
    deviceGroup.appendChild(outerRing);
    deviceGroup.appendChild(innerCircle);
    svg.appendChild(gatewayGroup);
    svg.appendChild(deviceGroup);
    return svg;
  }

  public static createStackedDeviceMarkerSVG(devices: any[], overrideTopDevice?: any): SVGElement {
    const itemForColor = overrideTopDevice || devices[0];

    const count = devices.length;
    const size = count >= 100 ? '48' : count >= 10 ? '32' : '24';
    const fontSize = '14px';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.style.transform = 'translateY(50%)';

    const hasSfData = this._devicesHaveSfData(devices);
    const center = { x: 16, y: 16 };
    const donutInnerRadius = 13;

    const centerFill = document.createElementNS(SVG_NS, 'circle');
    centerFill.setAttribute('cx', String(center.x));
    centerFill.setAttribute('cy', String(center.y));
    centerFill.setAttribute('r', String(donutInnerRadius));
    centerFill.setAttribute('fill', '#ffffff');
    svg.appendChild(centerFill);

    if (hasSfData) {
      svg.appendChild(this._createSfPieGroup(devices, 15, donutInnerRadius, center, true));
      svg.appendChild(this._createSfPieGroup(devices, 11, donutInnerRadius, center, false));
    } else {
      svg.appendChild(this._createStatusPieGroup(devices, 15, donutInnerRadius, center, true));
      svg.appendChild(this._createStatusPieGroup(devices, 11, donutInnerRadius, center, false));
    }

    const textElement = document.createElementNS(SVG_NS, 'text');
    textElement.setAttribute('x', '16');
    textElement.setAttribute('y', '17');
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-weight', 'bold');
    textElement.setAttribute('font-size', fontSize);
    textElement.setAttribute('font-family', 'sans-serif');
    textElement.setAttribute('fill', '#333333');
    textElement.textContent = count.toString();
    svg.appendChild(textElement);

    return svg;
  }

  public static createCombinedMarkerSVG(group: any[], overrideTopDevice?: any): SVGElement {
    const devices = group.filter((item) => item.type === 'device');
    const gateways = group.filter((item) => item.type === 'gateway');

    const count = group.length;
    const size = count >= 100 ? '60' : count >= 10 ? '50' : '42';

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('viewBox', '0 0 36 38');
    svg.style.transform = 'translateY(50%)';

    const hasSfData = this._devicesHaveSfData(devices);

    const gatewayGroup = document.createElementNS(SVG_NS, 'g');
    gatewayGroup.setAttribute('transform', 'translate(2, 2) rotate(45 16 16)');
    const rect = { x: 6, y: 6, w: 24, h: 24 };
    const innerRectObj = { x: 9, y: 9, w: 18, h: 18 };
    const gwCenter = { x: 18, y: 18 };

    gatewayGroup.appendChild(this._createPolygonalPieGroup(gateways, rect, innerRectObj, gwCenter, 'status'));
    const innerRect = document.createElementNS(SVG_NS, 'rect');
    innerRect.setAttribute('x', '9');
    innerRect.setAttribute('y', '9');
    innerRect.setAttribute('width', '18');
    innerRect.setAttribute('height', '18');
    innerRect.setAttribute('fill', '#ffffff');
    gatewayGroup.appendChild(innerRect);
    svg.appendChild(gatewayGroup);

    const deviceGroup = document.createElementNS(SVG_NS, 'g');
    deviceGroup.setAttribute('transform', 'translate(4, 4)');
    const donutInnerRadius = 8;
    const devCenter = { x: 14, y: 17 };

    const devCenterFill = document.createElementNS(SVG_NS, 'circle');
    devCenterFill.setAttribute('cx', String(devCenter.x));
    devCenterFill.setAttribute('cy', String(devCenter.y));
    devCenterFill.setAttribute('r', String(donutInnerRadius));
    devCenterFill.setAttribute('fill', 'rgba(255, 255, 255, 0.6)');
    deviceGroup.appendChild(devCenterFill);

    if (hasSfData) {
      deviceGroup.appendChild(this._createSfPieGroup(devices, 9, donutInnerRadius, devCenter, true));
      deviceGroup.appendChild(this._createSfPieGroup(devices, 7, donutInnerRadius, devCenter, false));
    } else {
      deviceGroup.appendChild(this._createStatusPieGroup(devices, 9, donutInnerRadius, devCenter, true));
      deviceGroup.appendChild(this._createStatusPieGroup(devices, 7, donutInnerRadius, devCenter, false));
    }
    svg.appendChild(deviceGroup);

    const textElement = document.createElementNS(SVG_NS, 'text');
    textElement.setAttribute('x', '18');
    textElement.setAttribute('y', '22');
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-weight', 'bold');
    textElement.setAttribute('font-size', count >= 100 ? '6px' : count >= 10 ? '8px' : '10px');
    textElement.setAttribute('font-family', 'sans-serif');
    textElement.setAttribute('fill', '#333333');
    textElement.textContent = `${group.length}`;
    svg.appendChild(textElement);

    return svg;
  }

  public static createDeviceMarkerSVG(statusColor: string, sFColor: string, scale?: number): SVGElement {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.style.transform = 'translateY(50%)';
    const outerColor = !!sFColor ? sFColor : statusColor;
    const outerRing = document.createElementNS(SVG_NS, 'circle');
    outerRing.setAttribute('cx', '12');
    outerRing.setAttribute('cy', '12');
    outerRing.setAttribute('r', '8');
    outerRing.setAttribute('fill', outerColor);
    const innerCircle = document.createElementNS(SVG_NS, 'circle');
    innerCircle.setAttribute('cx', '12');
    innerCircle.setAttribute('cy', '12');
    innerCircle.setAttribute('r', '6');
    innerCircle.setAttribute('fill', '#ffffff');
    svg.appendChild(outerRing);
    svg.appendChild(innerCircle);
    if (scale) {
      svg.setAttribute('transform', `scale(${scale})`);
    }
    return svg;
  }

  public static createStackedGatewayMarkerSVG(gateways: any[], overrideTopGateway?: any): SVGElement {
    const count = gateways.length;
    const fontSize = '14px';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.setAttribute('viewBox', '0 0 32 36');
    svg.style.transform = 'translateY(50%)';
    svg.style.transformOrigin = 'bottom';
    const group = document.createElementNS(SVG_NS, 'g');
    group.setAttribute('transform', 'rotate(45 16 16)');

    const rect = { x: 6, y: 6, w: 24, h: 24 };
    const innerRectObj = { x: 9, y: 9, w: 18, h: 18 };
    const center = { x: 18, y: 18 };
    group.appendChild(this._createPolygonalPieGroup(gateways, rect, innerRectObj, center, 'status'));

    const innerRect = document.createElementNS(SVG_NS, 'rect');
    innerRect.setAttribute('x', '9');
    innerRect.setAttribute('y', '9');
    innerRect.setAttribute('width', '18');
    innerRect.setAttribute('height', '18');
    innerRect.setAttribute('fill', '#ffffff');
    group.appendChild(innerRect);
    svg.appendChild(group);

    const textElement = document.createElementNS(SVG_NS, 'text');
    textElement.setAttribute('x', '16');
    textElement.setAttribute('y', '20');
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-weight', 'bold');
    textElement.setAttribute('font-size', fontSize);
    textElement.setAttribute('font-family', 'sans-serif');
    textElement.setAttribute('fill', '#333333');
    textElement.textContent = count.toString();
    svg.appendChild(textElement);

    return svg;
  }

  public static createGatewayIcon(fillColor: string, scale?: number): HTMLElement {
    const container = document.createElement('div');
    container.style.position = 'relative';
    container.style.width = `28px`;
    container.style.height = `28px`;
    const diamondSVG = this.createGatewayMarkerSVG(fillColor, scale);
    container.appendChild(diamondSVG);
    return container;
  }

  public static createGatewayMarkerSVG(statusColor: string, scale?: number, inner: boolean = true): SVGElement {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.setAttribute('viewBox', '0 0 32 34');
    const group = document.createElementNS(SVG_NS, 'g');
    group.setAttribute('transform', 'translate(0, 0) rotate(45 16 16) scale(0.9)');
    const outerRect = document.createElementNS(SVG_NS, 'rect');
    outerRect.setAttribute('x', '6');
    outerRect.setAttribute('y', '6');
    outerRect.setAttribute('width', '24');
    outerRect.setAttribute('height', '24');
    outerRect.setAttribute('fill', statusColor);
    group.appendChild(outerRect);
    if (inner) {
      const innerRect = document.createElementNS(SVG_NS, 'rect');
      innerRect.setAttribute('x', '9');
      innerRect.setAttribute('y', '9');
      innerRect.setAttribute('width', '18');
      innerRect.setAttribute('height', '18');
      innerRect.setAttribute('fill', '#ffffff');
      group.appendChild(innerRect);
    }
    svg.appendChild(group);
    if (scale) {
      svg.style.transform = `translateY(50%) scale(${scale})`;
    }
    return svg;
  }

  public static createClusterIcon(count: number): HTMLElement {
    const size = count >= 1000 ? 64 : count >= 100 ? 54 : 44;
    const fontSize = count >= 100 ? 15 : 14;
    const svg = window.btoa(`<svg fill="#016EA1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240"><circle cx="120" cy="120" r="70" opacity=".6"/><circle cx="120" cy="120" r="60" /></svg>`);
    const clusterIcon = document.createElement('img');
    clusterIcon.src = `data:image/svg+xml;base64,${svg}`;
    clusterIcon.style.cssText = `position: absolute; top: 50%; left: 50%; width: ${size}px; height: ${size}px; transform: translate(-50%, -50%); cursor: pointer;`;
    const label = document.createElement('div');
    label.textContent = String(count);
    label.style.cssText = `position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-family: Arial, sans-serif; font-size: ${fontSize}px; font-weight: bold;`;
    const container = document.createElement('div');
    container.appendChild(clusterIcon);
    container.appendChild(label);
    return container;
  }


  private static _polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians)),
    };
  }

  private static _describeArc(x: number, y: number, radius: number, innerRadius: number, startAngle: number, endAngle: number): string {
    if (endAngle - startAngle >= 360) {
      endAngle = 359.99;
    }
    const start = this._polarToCartesian(x, y, radius, endAngle);
    const end = this._polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    let d = [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    ].join(' ');
    if (innerRadius > 0) {
      const start2 = this._polarToCartesian(x, y, innerRadius, endAngle);
      const end2 = this._polarToCartesian(x, y, innerRadius, startAngle);

      d += ` L ${end2.x} ${end2.y}`;
      d += ` A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${start2.x} ${start2.y}`;
      d += ' Z';
    } else {
      d += ` L ${x} ${y} Z`;
    }

    return d;
  }

  private static _getPointOnPerimeter(percent: number, r: Rect): Point {
    const p = percent * (r.w * 2 + r.h * 2);
    if (p <= r.w) {
      return { x: r.x + p, y: r.y };
    }
    if (p <= r.w + r.h) {
      return { x: r.x + r.w, y: r.y + (p - r.w) };
    }
    if (p <= r.w * 2 + r.h) {
      return { x: r.x + r.w - (p - r.w - r.h), y: r.y + r.h };
    }
    return { x: r.x, y: r.y + r.h - (p - r.w * 2 - r.h) };
  }

  private static _createStatusPieGroup(items: any[], radius: number, innerRadius: number, center: Point, isOuterRing: boolean): SVGElement {
    const pieGroup = document.createElementNS(SVG_NS, 'g');
    const total = items.length;
    if (total === 0) {
      const path = document.createElementNS(SVG_NS, 'path');
      const d = this._describeArc(center.x, center.y, radius, innerRadius, 0, 360);
      path.setAttribute('d', d);
      path.setAttribute('fill', STATUS_COLORS.inactive);
      pieGroup.appendChild(path);
      return pieGroup;
    }
    const statusCounts = {
      active: items.filter((i) => i.status === 'active').length,
      fault: items.filter((i) => i.status === 'fault').length,
      inactive: items.filter((i) => i.status === 'inactive').length,
    };
    let startAngle = 0;
    const statusOrder: Array<keyof typeof statusCounts> = ['active', 'fault', 'inactive'];
    for (const status of statusOrder) {
      const count = statusCounts[status];
      if (count === 0) {
        continue;
      }
      const percent = count / total;
      const endAngle = startAngle + (percent * 360);
      const path = document.createElementNS(SVG_NS, 'path');
      const d = this._describeArc(center.x, center.y, radius, innerRadius, startAngle, endAngle);
      path.setAttribute('d', d);
      path.setAttribute('fill', STATUS_COLORS[status]);
      pieGroup.appendChild(path);
      startAngle = endAngle;
    }
    return pieGroup;
  }

  private static _createSfPieGroup(devices: any[], radius: number, innerRadius: number, center: Point, isOuterRing: boolean): SVGElement {
    const pieGroup = document.createElementNS(SVG_NS, 'g');
    const total = devices.length;

    const sfCounts = new Map<number, number>();
    const statusCounts = { active: 0, fault: 0, inactive: 0 };

    for (const d of devices) {
      const sf = d.network && d.network.loraSpreadingFactor;
      if (typeof sf === 'number' && sf >= 7 && sf <= 12) {
        sfCounts.set(sf, (sfCounts.get(sf) || 0) + 1);
      } else {
        if (d.status === 'fault') {
          statusCounts.fault++;
        } else if (d.status === 'inactive') {
          statusCounts.inactive++;
        } else {
          statusCounts.active++;
        }
      }
    }

    let startAngle = 0;

    const sortedSFs = Array.from(sfCounts.keys()).sort((a, b) => a - b);
    for (const sf of sortedSFs) {
      const count = sfCounts.get(sf)!;
      const percent = count / total;
      const endAngle = startAngle + (percent * 360);

      const path = document.createElementNS(SVG_NS, 'path');
      const d = this._describeArc(center.x, center.y, radius, innerRadius, startAngle, endAngle);
      path.setAttribute('d', d);
      path.setAttribute('fill', this.getSpreadingFactorColor(sf));
      pieGroup.appendChild(path);

      startAngle = endAngle;
    }

    const statusOrder: Array<keyof typeof statusCounts> = ['active', 'fault', 'inactive'];
    for (const status of statusOrder) {
      const count = statusCounts[status];
      if (count === 0) {
        continue;
      }

      const percent = count / total;
      const endAngle = startAngle + (percent * 360);

      const path = document.createElementNS(SVG_NS, 'path');
      const d = this._describeArc(center.x, center.y, radius, innerRadius, startAngle, endAngle);
      path.setAttribute('d', d);
      path.setAttribute('fill', STATUS_COLORS[status]);
      pieGroup.appendChild(path);

      startAngle = endAngle;
    }

    return pieGroup;
  }

  private static _createPolygonalPieGroup(items: any[], r: Rect, innerR: Rect, center: Point, type: 'status'): SVGElement {
    const pieGroup = document.createElementNS(SVG_NS, 'g');
    let data: Array<{ count: number; color: string }> = [];
    let total = 0;

    total = items.length;
    if (total === 0) {
      data = [{ count: 1, color: STATUS_COLORS.inactive }];
      total = 1;
    } else {
      const statusCounts = {
        active: items.filter((i) => i.status === 'active').length,
        fault: items.filter((i) => i.status === 'fault').length,
        inactive: items.filter((i) => i.status === 'inactive').length,
      };
      if (statusCounts.active > 0) {
        data.push({count: statusCounts.active, color: STATUS_COLORS.active});
      }
      if (statusCounts.fault > 0) {
        data.push({count: statusCounts.fault, color: STATUS_COLORS.fault});
      }
      if (statusCounts.inactive > 0) {
        data.push({count: statusCounts.inactive, color: STATUS_COLORS.inactive});
      }
    }

    const corners = [
      { p: 0.25, pos: { x: r.x + r.w, y: r.y } },
      { p: 0.50, pos: { x: r.x + r.w, y: r.y + r.h } },
      { p: 0.75, pos: { x: r.x, y: r.y + r.h } },
      { p: 1.00, pos: { x: r.x, y: r.y } },
    ];
    const innerCorners = [
      { p: 0.25, pos: { x: innerR.x + innerR.w, y: innerR.y } },
      { p: 0.50, pos: { x: innerR.x + innerR.w, y: innerR.y + innerR.h } },
      { p: 0.75, pos: { x: innerR.x, y: innerR.y + innerR.h } },
      { p: 1.00, pos: { x: innerR.x, y: innerR.y } },
    ];

    let startPercent = 0;
    for (const slice of data) {
      const percent = slice.count / total;
      const endPercent = startPercent + percent;
      const p1Out = this._getPointOnPerimeter(startPercent, r);
      const p2Out = this._getPointOnPerimeter(endPercent, r);
      const p1In = this._getPointOnPerimeter(startPercent, innerR);
      const p2In = this._getPointOnPerimeter(endPercent, innerR);
      const pathPoints: string[] = [`M ${p1In.x},${p1In.y}`];
      pathPoints.push(`L ${p1Out.x},${p1Out.y}`);
      for (const corner of corners) {
        if (corner.p > startPercent && corner.p < endPercent) {
          pathPoints.push(`L ${corner.pos.x},${corner.pos.y}`);
        }
      }
      pathPoints.push(`L ${p2Out.x},${p2Out.y}`);
      pathPoints.push(`L ${p2In.x},${p2In.y}`);

      for (let i = innerCorners.length - 1; i >= 0; i--) {
        const corner = innerCorners[i];
        if (corner.p > startPercent && corner.p < endPercent) {
          pathPoints.push(`L ${corner.pos.x},${corner.pos.y}`);
        }
      }

      pathPoints.push('Z');

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', pathPoints.join(' '));
      path.setAttribute('fill', slice.color);
      pieGroup.appendChild(path);

      startPercent = endPercent;
    }
    return pieGroup;
  }

  private static _devicesHaveSfData(devices: any[]): boolean {
    return devices.some((d) =>
      d.network && typeof d.network.loraSpreadingFactor === 'number' &&
      d.network.loraSpreadingFactor >= 7 &&
      d.network.loraSpreadingFactor <= 12,
    );
  }
}
